import { gsap } from "gsap";
import { Check, X } from "lucide-react";
import { useLayoutEffect, useRef } from "react";

import { MOTION_DURATION, MOTION_EASE, MOTION_STAGGER } from "@/shared/components/motion/motion.tokens";
import { usePrefersReducedMotion } from "@/shared/hooks/usePrefersReducedMotion";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { LoadTimelineStageState } from "../../utils/cardFunding";

export interface CardLoadTimelineStep {
  id: string;
  label: string;
  state: LoadTimelineStageState;
  /** A second line: a time, a hint. */
  detail?: string | null;
}

export interface CardLoadTimelineProps {
  steps: CardLoadTimelineStep[];
  "aria-label": string;
  className?: string;
}

const STATE_KEY = {
  done: "card_load_stage_state_done",
  active: "card_load_stage_state_active",
  pending: "card_load_stage_state_pending",
  failed: "card_load_stage_state_failed",
} as const;

/**
 * The money's trail as a vertical line: each stage a dot, the line filling down to wherever the money is. The
 * fill follows the state (it never runs ahead on a timer), so it can only claim what the backend said.
 * Without motion the line is simply drawn to where it belongs.
 */
export function CardLoadTimeline({ steps, className, "aria-label": ariaLabel }: CardLoadTimelineProps) {
  const { t } = useTranslations("cards");
  const ref = useRef<HTMLOListElement>(null);
  const reduced = usePrefersReducedMotion();
  const signature = steps.map((step) => step.state).join("|");

  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const fills = root.querySelectorAll<HTMLElement>("[data-fill]");
    if (reduced) {
      fills.forEach((fill) => gsap.set(fill, { scaleY: fill.dataset.fill === "on" ? 1 : 0 }));
      return;
    }
    const ctx = gsap.context(() => {
      const on = Array.from(fills).filter((fill) => fill.dataset.fill === "on");
      const off = Array.from(fills).filter((fill) => fill.dataset.fill !== "on");
      gsap.set(off, { scaleY: 0 });
      gsap.to(on, {
        scaleY: 1,
        duration: MOTION_DURATION.enter,
        ease: MOTION_EASE.enter,
        stagger: MOTION_STAGGER.loose,
      });
    }, root);
    // Not reverted on a state change: the fills already drawn stay drawn and only the new segment grows.
    return () => ctx.kill();
  }, [signature, reduced]);

  return (
    <ol ref={ref} aria-label={ariaLabel} className={cn("flex flex-col", className)}>
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        // A segment is filled once the money has left the stage above it.
        const filled = step.state === "done" && !last && steps[index + 1]?.state !== "failed";
        return (
          <li key={step.id} className="relative flex gap-3 pb-5 last:pb-0">
            {!last && (
              <span
                aria-hidden="true"
                className="absolute left-[11px] top-6 bottom-0 w-0.5 overflow-hidden bg-border"
              >
                <span
                  data-fill={filled ? "on" : "off"}
                  className="block h-full w-full origin-top bg-primary"
                  style={{ transform: `scaleY(${filled ? 1 : 0})` }}
                />
              </span>
            )}
            <StageDot state={step.state} />
            <div className="min-w-0 pt-0.5">
              <p
                className={cn(
                  "text-sm",
                  step.state === "pending" ? "text-muted-foreground" : "font-medium text-foreground",
                  step.state === "failed" && "text-destructive",
                )}
              >
                {step.label}
                <span className="sr-only"> · {t(STATE_KEY[step.state])}</span>
              </p>
              {step.detail && <p className="text-xs text-muted-foreground">{step.detail}</p>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function StageDot({ state }: { state: LoadTimelineStageState }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative z-10 inline-flex size-6 shrink-0 items-center justify-center rounded-full border-2 bg-card transition-colors duration-300",
        state === "done" && "border-primary bg-primary text-primary-foreground",
        state === "active" && "border-primary text-primary",
        state === "pending" && "border-border",
        state === "failed" && "border-destructive bg-destructive text-destructive-foreground",
      )}
    >
      {state === "done" && <Check className="size-3.5" strokeWidth={3} />}
      {state === "failed" && <X className="size-3.5" strokeWidth={3} />}
      {state === "active" && (
        <span className="size-2 rounded-full bg-primary animate-pulse motion-reduce:animate-none" />
      )}
    </span>
  );
}
